import {ApiError} from '@/shared/api';

/**
 * What to show a human when a call failed.
 *
 * The gateway answers `{code, message}` and its message is written for people
 * ("invite code is unknown, already used, or expired") — showing it beats replacing
 * it with a generic apology that hides which of the three it was. Anything that is
 * not an ApiError never reached the server: that is the network, and only then do we
 * substitute our own wording.
 */
export function messageOf(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * A 409 that names the row already holding a value the user just typed.
 *
 * Two writes in this API answer that way, and both phrase it identically:
 *
 *   employee number 'EMP-77' already belongs to record 7d359ce0-…
 *   department code 'HR'     already belongs to department 7d359ce0-…
 *
 * (`POST /control/personnel` and `PUT /control/personnel/{id}` after 2026-08-11,
 * `save_department` after the partial-index migration of the same day.)
 *
 * The reason to take the sentence apart rather than print it is the OWNER ID. A
 * toast saying "already taken" leaves the operator to find the culprit by scrolling
 * a list; the id in the message is the shortest path to it, and it is only useful
 * once it has been separated from the prose around it.
 *
 * The parse is deliberately forgiving about what comes BEFORE the subject and
 * strict about the shape after it: the gateway may one day prefix its own context,
 * but `'<value>' already belongs to <kind> <id>` is the part the changelog
 * documents.
 */
export type OwnedConflict = {
  /** What is taken, as the server named it: `employee number`, `department code`. */
  subject: string;
  /** The value the user typed that collided, verbatim and without the quotes. */
  value: string;
  /** The kind of row holding it: `record`, `department`. */
  ownerKind: string;
  /** The id of that row — what an "open it" affordance needs. */
  ownerId: string;
};

const OWNED_BY =
  /([a-z][a-z ]*?) '([^']*)' already belongs to ([a-z][a-z_-]*) ([^\s,;]+)/i;

/**
 * Parse a conflict of the shape above, or null for anything else — including a 409
 * that is not this shape (an ambiguous id across companies, say: see
 * `conventions.md` §4). A caller that gets null must still show the raw message.
 */
export function ownedConflict(error: unknown): OwnedConflict | null {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return null;
  }
  const m = OWNED_BY.exec(error.message);
  if (!m) {
    return null;
  }
  return {
    subject: m[1].trim(),
    value: m[2],
    ownerKind: m[3],
    ownerId: m[4],
  };
}

/**
 * The message for a field-level error box, or null when this error does not belong
 * to that field.
 *
 * `ownerKind` is the discriminator rather than `subject`, because the subject is
 * English prose from the server and the caller is a Russian form: matching on
 * `record` / `department` keeps the coupling to the one token least likely to be
 * rewritten for readability.
 */
export function fieldConflict(
  error: unknown,
  ownerKind: string,
): OwnedConflict | null {
  const conflict = ownedConflict(error);
  return conflict && conflict.ownerKind === ownerKind ? conflict : null;
}
